/**
 * useSessionContext
 * ─────────────────
 * Tracks ephemeral context the user has attached to the welcome composer
 * before sending: vault documents, a single uploaded file, the picked
 * document type, and the customise settings.
 *
 * Returns:
 *  - state object
 *  - mutators
 *  - `buildPayload()` — produces the request fields to merge into
 *    `sendMessage`'s `extra` parameter
 *  - `summary()` — short human label of which knobs are non-default
 */
import { useCallback, useMemo, useState } from 'react';

const DEFAULT_SETTINGS = {
  language:     'arabic',         // arabic | english | bilingual
  detail:       'professional',   // summary | detailed | professional
  tone:         'formal',         // formal | commercial | simplified
  jurisdiction: 'saudi',          // saudi | bahrain | uae | general
};

export function useSessionContext() {
  const [vaultDocuments, setVaultDocuments] = useState([]); // [{id,name,content,...}]
  const [uploadedFile, setUploadedFile]     = useState(null); // {name,content,size}
  const [documentType, setDocumentType]     = useState(null); // {id,label,en}
  const [settings, setSettings]             = useState(DEFAULT_SETTINGS);

  const addVaultDocs = useCallback((docs) => {
    setVaultDocuments((cur) => {
      const seen = new Set(cur.map((d) => d.id));
      return [...cur, ...docs.filter((d) => !seen.has(d.id))];
    });
  }, []);
  const removeVaultDoc = useCallback((id) => {
    setVaultDocuments((cur) => cur.filter((d) => d.id !== id));
  }, []);

  const updateSetting = useCallback((key, value) => {
    setSettings((s) => ({ ...s, [key]: value }));
  }, []);
  const resetSettings = useCallback(() => setSettings(DEFAULT_SETTINGS), []);

  const hasCustomSettings = useMemo(() => (
    Object.keys(DEFAULT_SETTINGS).some((k) => settings[k] !== DEFAULT_SETTINGS[k])
  ), [settings]);

  const hasActiveContext = (
    vaultDocuments.length > 0 ||
    Boolean(uploadedFile) ||
    Boolean(documentType) ||
    hasCustomSettings
  );

  /**
   * Concatenate the attached vault docs + uploaded file into one string,
   * and surface the picked document type / customise knobs as a structured
   * preamble. Caller passes this into `sendMessage`'s `documentText` and
   * `extra` parameters.
   */
  const buildPayload = useCallback(() => {
    const parts = [];
    if (vaultDocuments.length) {
      parts.push(...vaultDocuments.map((d) => `## ${d.name}\n\n${d.content || ''}`));
    }
    if (uploadedFile?.content && !uploadedFile.error && !uploadedFile.extracting) {
      parts.push(`## ${uploadedFile.name}\n\n${uploadedFile.content}`);
    }
    const documentText = parts.join('\n\n---\n\n');

    const extra = {};
    if (documentType) extra.document_type = documentType.id;
    if (hasCustomSettings) extra.output_settings = settings;

    return {
      documentText: documentText || null,
      contractType: documentType?.id || null,
      extra,
    };
  }, [vaultDocuments, uploadedFile, documentType, settings, hasCustomSettings]);

  const clearAll = useCallback(() => {
    setVaultDocuments([]);
    setUploadedFile(null);
    setDocumentType(null);
    setSettings(DEFAULT_SETTINGS);
  }, []);

  return {
    vaultDocuments, addVaultDocs, removeVaultDoc,
    uploadedFile, setUploadedFile,
    documentType, setDocumentType,
    settings, updateSetting, resetSettings, hasCustomSettings,
    hasActiveContext, buildPayload, clearAll,
  };
}

export { DEFAULT_SETTINGS };
export default useSessionContext;
